import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Shuffle, Brain, Layers, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useProgress } from "@/hooks/useProgress";
import { useSRS } from "@/hooks/useSRS";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FlashcardsPage() {
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("browse");
  const [dueCount, setDueCount] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [reviewDone, setReviewDone] = useState(false);
  const { markAsLearned, isLearned } = useProgress();
  const { user, syncProgress } = useAuth();
  const { getDue, rate } = useSRS();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setReviewDone(false);
      setReviewedCount(0);
      setCurrentIndex(0);
      setFlipped(false);
      try {
        if (mode === "review") {
          const [due, cRes] = await Promise.all([getDue(), axios.get(`${API}/categories`)]);
          setWords(due.words);
          setDueCount(due.due_count);
          setCategories(cRes.data);
        } else {
          const catParam = selectedCat !== "all" ? `?category=${selectedCat}&count=100` : "?count=100";
          const [wRes, cRes] = await Promise.all([
            axios.get(`${API}/words/random${catParam}`),
            axios.get(`${API}/categories`),
          ]);
          setWords(wRes.data);
          setCategories(cRes.data);
        }
      } catch (e) {
        console.error("Failed to load", e);
      }
      setLoading(false);
    };
    load();
  }, [selectedCat, mode, getDue]);

  useEffect(() => {
    getDue().then((d) => setDueCount(d.due_count)).catch(() => {});
  }, [getDue]);

  const handleRate = async (rating) => {
    const word = words[currentIndex];
    if (!word) return;
    try {
      await rate(word.id, rating);
    } catch (e) {
      console.error("Failed to save review", e);
    }
    setReviewedCount((c) => c + 1);
    setFlipped(false);
    if (currentIndex < words.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setReviewDone(true);
    }
  };

  const currentWord = words[currentIndex];
  const progress = words.length > 0 ? ((currentIndex + 1) / words.length) * 100 : 0;

  const goNext = useCallback(() => {
    if (currentIndex < words.length - 1) { setFlipped(false); setCurrentIndex((i) => i + 1); }
  }, [currentIndex, words.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) { setFlipped(false); setCurrentIndex((i) => i - 1); }
  }, [currentIndex]);

  const handleShuffle = () => {
    setWords((w) => [...w].sort(() => Math.random() - 0.5));
    setCurrentIndex(0);
    setFlipped(false);
    toast.success("Cards shuffled!");
  };

  const handleCategoryChange = (val) => {
    setSelectedCat(val);
    setCurrentIndex(0);
    setFlipped(false);
  };

  const handleLearn = () => {
    if (currentWord) {
      markAsLearned(currentWord.id);
      if (user) syncProgress(currentWord.id);
      toast.success(`"${currentWord.swedish}" marked as learned!`);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === " ") { e.preventDefault(); setFlipped((f) => !f); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-nordic-text-muted">Loading flashcards...</div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="flashcards-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-sm uppercase tracking-[0.2em] text-nordic-text-muted font-semibold mb-3">Flashcards</p>
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-nordic-text tracking-tight mb-8">Learn at your pace</h1>
        </motion.div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-6" data-testid="flashcard-mode-toggle">
          <button
            onClick={() => setMode("browse")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium ${
              mode === "browse" ? "bg-nordic-blue text-white" : "bg-nordic-paper border border-nordic-wood/30 text-nordic-text-secondary hover:bg-nordic-wood/20"
            }`}
            style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
            data-testid="mode-browse-btn"
          >
            <Layers className="w-4 h-4" /> Browse
          </button>
          <button
            onClick={() => setMode("review")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium ${
              mode === "review" ? "bg-nordic-blue text-white" : "bg-nordic-paper border border-nordic-wood/30 text-nordic-text-secondary hover:bg-nordic-wood/20"
            }`}
            style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
            data-testid="mode-review-btn"
          >
            <Brain className="w-4 h-4" /> Smart Review
            {mode !== "review" && dueCount > 0 && (
              <span className="bg-nordic-accent text-white text-xs rounded-full px-2 py-0.5">{dueCount}</span>
            )}
          </button>
        </div>

        {/* Controls */}
        {mode === "browse" && (
        <div className="flex flex-wrap items-center gap-4 mb-8" data-testid="flashcard-controls">
          <Select value={selectedCat} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[220px] rounded-xl bg-nordic-paper border-nordic-wood/30" data-testid="category-select">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.slug} value={cat.slug}>{cat.name} ({cat.word_count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleShuffle} className="rounded-full border-nordic-wood/30 text-nordic-text-secondary hover:bg-nordic-wood/20" data-testid="shuffle-btn">
            <Shuffle className="w-4 h-4 mr-2" /> Shuffle
          </Button>
          <span className="text-sm text-nordic-text-muted ml-auto" data-testid="card-counter">{currentIndex + 1} / {words.length}</span>
        </div>
        )}

        {mode === "review" && !loading && words.length > 0 && !reviewDone && (
          <div className="flex items-center justify-between mb-8" data-testid="review-header">
            <p className="text-sm text-nordic-text-secondary">
              <span className="font-semibold text-nordic-text">{dueCount}</span> cards due for review
            </p>
            <span className="text-sm text-nordic-text-muted" data-testid="review-counter">{currentIndex + 1} / {words.length}</span>
          </div>
        )}

        <Progress value={progress} className="h-1.5 mb-10 bg-nordic-wood/20" data-testid="flashcard-progress" />

        {mode === "review" && reviewDone ? (
          <div className="text-center py-20" data-testid="review-done">
            <div className="w-16 h-16 rounded-full bg-nordic-success/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-nordic-success" />
            </div>
            <h2 className="font-heading text-3xl text-nordic-text mb-3">Review complete!</h2>
            <p className="text-nordic-text-secondary mb-8">You reviewed {reviewedCount} {reviewedCount === 1 ? "card" : "cards"}. Great work!</p>
            <Button onClick={() => setMode("browse")} variant="outline" className="rounded-full border-nordic-wood/30" data-testid="back-to-browse-btn">
              <Layers className="w-4 h-4 mr-2" /> Back to Browse
            </Button>
          </div>
        ) : mode === "review" && words.length === 0 ? (
          <div className="text-center py-20" data-testid="review-empty">
            <div className="w-16 h-16 rounded-full bg-nordic-blue/10 flex items-center justify-center mx-auto mb-6">
              <Brain className="w-8 h-8 text-nordic-blue" />
            </div>
            <h2 className="font-heading text-3xl text-nordic-text mb-3">All caught up!</h2>
            <p className="text-nordic-text-secondary max-w-md mx-auto mb-8">
              No cards are due for review. Mark words as learned in Browse mode or the Gallery to build your review deck — they'll come back at the perfect time to remember them.
            </p>
            <Button onClick={() => setMode("browse")} variant="outline" className="rounded-full border-nordic-wood/30" data-testid="start-browsing-btn">
              <Layers className="w-4 h-4 mr-2" /> Browse Cards
            </Button>
          </div>
        ) : currentWord ? (
          <div className="flex flex-col items-center">
            <div className="flashcard-container w-full max-w-lg cursor-pointer" style={{ height: "420px" }}
              onClick={() => setFlipped((f) => !f)} data-testid="flashcard">
              <div className={`flashcard-inner ${flipped ? "flipped" : ""}`}>
                <div className="flashcard-front bg-nordic-paper shadow-lg border border-nordic-wood/10">
                  <img src={currentWord.image_url} alt={currentWord.english} className="w-full h-[300px] object-cover"
                    onError={(e) => { const f = "https://images.unsplash.com/photo-1665064914191-6512af753429?w=600&fit=crop&q=80"; if (e.target.src !== f) e.target.src = f; }} />
                  <div className="p-6 text-center">
                    <p className="text-nordic-text-muted text-sm mb-1">Tap to reveal</p>
                    <p className="font-heading text-3xl text-nordic-text font-medium">?</p>
                  </div>
                </div>
                <div className="flashcard-back bg-nordic-paper shadow-lg border border-nordic-wood/10 flex flex-col items-center justify-center p-10">
                  <p className="text-sm uppercase tracking-wider text-nordic-text-muted mb-4">{currentWord.category.replace("_", " ")}</p>
                  <h2 className="font-heading text-5xl text-nordic-text font-medium mb-2">{currentWord.swedish}</h2>
                  <p className="text-nordic-text-secondary text-xl mb-1">{currentWord.english}</p>
                  <p className="text-nordic-text-muted text-sm mb-6">/{currentWord.pronunciation}/</p>
                  <div className="bg-nordic-muted rounded-xl p-4 w-full max-w-sm">
                    <p className="text-sm text-nordic-text italic font-accent">"{currentWord.example_sentence}"</p>
                    <p className="text-xs text-nordic-text-muted mt-1">{currentWord.example_translation}</p>
                  </div>
                </div>
              </div>
            </div>

            {mode === "review" ? (
              flipped ? (
                <div className="flex items-center gap-3 mt-10" data-testid="review-rating-buttons">
                  <Button onClick={() => handleRate("again")} className="bg-nordic-error text-white hover:bg-nordic-error/90 rounded-full px-6 py-3" data-testid="rate-again-btn">
                    <RotateCcw className="w-4 h-4 mr-2" /> Again
                  </Button>
                  <Button onClick={() => handleRate("good")} className="bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full px-6 py-3" data-testid="rate-good-btn">
                    Good
                  </Button>
                  <Button onClick={() => handleRate("easy")} className="bg-nordic-success text-white hover:bg-nordic-success/90 rounded-full px-6 py-3" data-testid="rate-easy-btn">
                    Easy
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-nordic-text-muted mt-10" data-testid="review-flip-hint">Flip the card, then rate how well you remembered it</p>
              )
            ) : (
            <div className="flex items-center gap-4 mt-10">
              <Button variant="outline" size="icon" onClick={goPrev} disabled={currentIndex === 0} className="rounded-full w-12 h-12 border-nordic-wood/30" data-testid="prev-card-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              {isLearned(currentWord.id) ? (
                <div className="flex items-center gap-2 px-6 py-3 bg-nordic-success/10 text-nordic-success rounded-full text-sm font-medium" data-testid="already-learned">
                  <Check className="w-4 h-4" /> Learned
                </div>
              ) : (
                <Button onClick={handleLearn} className="bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full px-6 py-3" data-testid="mark-learned-btn">
                  <Check className="w-4 h-4 mr-2" /> Mark as Learned
                </Button>
              )}
              <Button variant="outline" size="icon" onClick={goNext} disabled={currentIndex >= words.length - 1} className="rounded-full w-12 h-12 border-nordic-wood/30" data-testid="next-card-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            )}
            {mode === "browse" && <p className="text-xs text-nordic-text-muted mt-6">Use arrow keys to navigate, spacebar to flip</p>}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-cards">
            <p className="text-nordic-text-muted text-lg">No cards available.</p>
          </div>
        )}
      </div>
    </div>
  );
}
